






























import { computed, defineComponent, onBeforeUnmount, ref } from "@vue/composition-api";
import { Notification } from "@scrile/api-provider/dist/api/NotificationsProvider";
import { UserPublicData } from "@scrile/api-provider/dist/api/UserPublicDataProvider";
import useAuthUser from "@/hooks/useAuthUser";
import providers from "@/lib/api";
import emitter from "@/lib/emitter";
import { $t } from "@/lib/i18nHelpers";
import { EVENTS } from "@/const";
import BaseButton from "@/components/BaseButton";
import BaseCheckbox from "@/components/BaseCheckbox";
import BaseConfirm from "@/components/BaseConfirm";

export default defineComponent({
  name: "BlockUserController",
  components: { BaseConfirm, BaseButton, BaseCheckbox },
  props: {
    opponent: { type: Object as () => UserPublicData, required: true },
    showConfirm: { type: Boolean, default: false },
  },
  setup(props, { emit }) {
    const { authUser, isCreator } = useAuthUser();

    const loading = ref(false);
    const isOpponentBlocked = ref(false);
    const isOpponentSubscriber = ref(false);
    const isAuthUserBlocked = ref(false);
    const shouldDeleteComments = ref(false);
    const shouldArchiveThread = ref(false);

    const acceptButtonText = computed(() => $t(isOpponentBlocked.value ? "Unblock" : "Block"));
    const blockingConfirmText = computed(() =>
      isOpponentBlocked.value
        ? $t("Unblock {0}", [props.opponent?.username])
        : $t("Block {0}", [props.opponent?.username])
    );

    const emitBlockingStatusChange = () => {
      emit("blockingStatusChange", {
        authUser: isAuthUserBlocked.value,
        opponent: isOpponentBlocked.value,
      });
    };

    const getBlockingStatus = async () => {
      if (!authUser.value) return;
      const statuses = await providers.BlockingStatusProvider.searchByOpponents({
        data: { opponentIds: [props.opponent.id] },
      });
      isOpponentBlocked.value = statuses.some(
        s => s.userId === authUser.value?.id && s.blockedUserId === props.opponent.id
      );
      isAuthUserBlocked.value = statuses.some(
        s => s.userId === props.opponent.id && s.blockedUserId === authUser.value?.id
      );
      emitBlockingStatusChange();
    };

    const hideOpponentComments = async () => {
      if (!authUser.value) return;
      await providers.ReviewProvider.hide({
        data: { reviewerId: props.opponent.id, subjectOwnerId: authUser.value.id },
      });
    };

    const handleHideThread = async () => {
      if (!authUser.value) return;
      providers.MessageThreadsProvider.setReceiveErrors(false);
      const thread = await providers.MessageThreadsProvider.findPersonal({ opponentId: props.opponent.id }, [
        "id",
        "hidden",
      ]).catch(() => null);
      if (thread && !thread.hidden) {
        const response = await providers.MessageThreadsProvider.hide({ id: thread.id });
        emitter.emit(EVENTS.MESSAGES_UPDATE_THREAD, response);
      }
    };

    const accept = async () => {
      try {
        loading.value = true;

        if (isOpponentBlocked.value) {
          await providers.BlockingStatusProvider.unblock(props.opponent.id);
          isOpponentBlocked.value = false;
        } else {
          const promises: Promise<any>[] = [providers.BlockingStatusProvider.block(props.opponent.id)];
          if (shouldDeleteComments.value) promises.push(hideOpponentComments());
          if (shouldArchiveThread.value) promises.push(handleHideThread());
          await Promise.all(promises);
          isOpponentBlocked.value = true;
        }
        emitBlockingStatusChange();
      } finally {
        loading.value = false;
        emit("close");
      }
    };

    const blockingStatusChangeHandler = (notification?: Notification): void => {
      if (!notification) return;
      if (notification.subjectId === authUser.value?.id && notification.recipientId === props.opponent.id) {
        isOpponentBlocked.value = !!notification.details?.blocked;
      } else if (notification.subjectId === props.opponent.id && notification.recipientId === authUser.value?.id) {
        isAuthUserBlocked.value = !!notification.details?.blocked;
      } else {
        return;
      }
      emitBlockingStatusChange();
    };

    const getOpponentSubscription = async () => {
      if (!isCreator.value) return;
      const opponentSubscriptionResponse = await providers.SubscriptionProvider.search(
        {
          data: {
            consumerIds: [props.opponent.id],
            providerId: authUser.value?.id ?? "",
            expired: false,
            size: 1,
          },
        },
        ["id"]
      );
      isOpponentSubscriber.value = !!opponentSubscriptionResponse.result.length;
    };

    Promise.all([getBlockingStatus(), getOpponentSubscription()]);
    emitter.on<Notification>(EVENTS.BLOCKING_STATUS_CHANGED, blockingStatusChangeHandler);
    onBeforeUnmount(() => emitter.off<Notification>(EVENTS.BLOCKING_STATUS_CHANGED, blockingStatusChangeHandler));

    return {
      loading,
      isOpponentBlocked,
      acceptButtonText,
      blockingConfirmText,
      isOpponentSubscriber,
      shouldDeleteComments,
      shouldArchiveThread,
      accept,
    };
  },
});
